import { useMutation } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { isEmpty } from 'es-toolkit/compat';
import { useAtomValue, useStore } from 'jotai';
import { useTranslations } from 'next-intl';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useKYC } from '@flipster/features/src/shared/kyc';
import { Button } from '@flipster/react/design-system/button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import Formatted from '@flipster/react/design-utils/formatted';
import { ServerError } from '@flipster/utils/constants/server-error';
import { datadog } from '@flipster/utils/datadog';
import segment, { WalletEvent } from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import { spotSymbolSocketBunja, tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import { TradeUnexpectedError } from '../errors';
import { OrderEventTracker } from '../tracker';
import { spotActionBunja } from './bunja/action';
import { spotEventBunja } from './bunja/event';
import { spotFormBunja, type Side } from './bunja/form';
import { validationBunja } from './bunja/validation';
import { actions } from './styles';

const Actions = () => {
  const transTrade = useTranslations('Trade.r250401');
  const transTradeR250704 = useTranslations('Trade.r250704');
  const store = useStore();
  const { symbol, symbolInfoWithDefaultAtom } = useBunja(tradeSymbolBunja);
  const { sideAtom, priceTypeAtom } = useBunja(spotFormBunja);
  const { submitOrder } = useBunja(spotActionBunja);
  // 제출(클릭) 시점에만 필요한 값 — 구독하지 않고 store.get으로 읽는다 (매 tick 리렌더 방지)
  const { orderEventContextInfoAtom, trackOrderClick, onOrderFailed } = useBunja(spotEventBunja);
  const { validationErrorsAtom, checkValidationAll } = useBunja(validationBunja);

  const symbolInfo = useAtomValue(symbolInfoWithDefaultAtom);
  const side = useAtomValue(sideAtom);
  const priceType = useAtomValue(priceTypeAtom);

  const { kycInfo, isRequireKyc } = useKYC();
  const { isCookieLoggedIn } = useAuth();

  const { displayShortName } = symbolInfo;
  const isMarketPrice = priceType === 'market';

  const { isPending, mutateAsync: onSubmit } = useMutation({
    mutationKey: ['submitSpotOrder'],
    mutationFn: async () => {
      const orderEventContextInfo = store.get(orderEventContextInfoAtom);
      const validationErrors = store.get(validationErrorsAtom);
      await OrderEventTracker.instance.withContext(orderEventContextInfo, async (ctx) => {
        trackOrderClick();
        if (isEmpty(validationErrors)) {
          await submitOrder(ctx);
        } else {
          const errors = Object.values(validationErrors);
          const toastError = errors.find((err) => err.type === 'toast');
          if (toastError?.message) {
            snackbar.error({ message: toastError.message });
            onOrderFailed(ctx, { type: 'validation', error: toastError.message });
            return;
          }
          const customError = errors.find((err) => err.type === 'custom');
          if (customError?.message) {
            snackbar.error({ message: customError.message });
            onOrderFailed(ctx, { type: 'validation', error: customError.message });
            return;
          }
          const unknownError = errors.find((err) => err.type !== 'custom');
          if (unknownError) {
            snackbar.error({ message: ServerError['api.server.error'] });
            onOrderFailed(ctx, { type: 'unknown', error: ServerError['api.server.error'] });
            // oxlint-disable-next-line no-console
            console.error(unknownError); // 예상 외 에러가 있을 경우 디버깅하기위해서 에러를 추가
            datadog.addError(new TradeUnexpectedError(`${unknownError.type}: ${unknownError.message}`));
          }
        }
      });
    },
  });

  if (!isCookieLoggedIn)
    return (
      <div className={actions}>
        <Button
          hierarchy='primary'
          color='accent'
          size='large'
          as='a'
          href='/signin'
          label={transTrade('Signup_or_login_to_trade_button')}
          onClick={() => segment.track(TradeEvent.OrderFormSignInClick, { symbol })}
          css={{ w: '100%', zIndex: 100 }}
        />
      </div>
    );

  if (isRequireKyc)
    return (
      <div className={actions}>
        <Button
          hierarchy='primary'
          color='accent'
          size='large'
          as='a'
          href='/user/kyc'
          label={transTrade('Verify_to_trade_button')}
          onClick={() => segment.track(WalletEvent.KycVerificationRequireClick, { kyc_level: kycInfo.currentKYCLevel })}
          css={{ w: '100%', zIndex: 100 }}
        />
      </div>
    );

  if (!['Buy', 'Sell'].includes(side)) return null;

  return (
    <div className={actions}>
      <Button
        htmlType='submit'
        hierarchy='primary'
        color={side === 'Buy' ? 'buy' : 'sell'}
        size='large'
        onClick={async () => {
          checkValidationAll();
          await onSubmit();
        }}
        label={
          side === 'Buy'
            ? transTradeR250704('Buy_symbol_button', { symbol: displayShortName })
            : transTradeR250704('Sell_symbol_button', { symbol: displayShortName })
        }
        subLabel={isMarketPrice ? <SpotMarketPriceSubLabel side={side} /> : undefined}
        isLoading={isPending}
        css={{ w: '100%' }}
      />
    </div>
  );
};

/**
 * market 모드 버튼의 실시간 호가 라벨.
 * raw spot detail 구독을 이 컴포넌트로 격리해 tick당 리렌더 범위를 라벨로 한정한다.
 */
const SpotMarketPriceSubLabel = ({ side }: { side: Side }) => {
  const { symbolInfoWithDefaultAtom } = useBunja(tradeSymbolBunja);
  const { pricePrecision } = useAtomValue(symbolInfoWithDefaultAtom);
  const { getDetailAtom } = useBunja(spotSymbolSocketBunja);
  const spotDetail = useAtomValue(getDetailAtom);
  return (
    <Formatted.Number
      value={side === 'Buy' ? spotDetail?.askPrice : spotDetail?.bidPrice}
      precision={pricePrecision}
      toFixed
    />
  );
};

export default Actions;
